import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import PersonaRoutine, { Moment } from '@comp/persona/persona-routine'
import PersonaHelpLinks, { HelpLinkGroup } from '@comp/persona/persona-help-links'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  routineEyebrow: string
  routineTitle: string
  routineBody: string
  moments: Moment[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  helpEyebrow: string
  helpTitle: string
  helpBody: string
  helpGroups: HelpLinkGroup[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Monofor for compliance officers',
    metaDescription:
      'Framework-aligned controls, tamper-evident audit, access reviews that finish, and enforced separation of duties: turn audit response from a quarterly fire drill into a steady state.',
    heroEyebrow: 'For compliance officers',
    heroTitle: ['Audit-ready,', 'not audit-panicked.'],
    heroBody:
      'Framework-aligned controls, tamper-evident audit logs, scheduled access reviews, and enforced separation of duties, on one platform built for the way auditors actually work.',
    badges: ['SOC 2 / ISO 27001 / GDPR / KVKK', 'Tamper-evident evidence', 'SoD enforcement'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    routineEyebrow: 'A day with Monofor',
    routineTitle: "A compliance officer's typical week.",
    routineBody:
      "No more audit-week fire drills, no more spreadsheet-driven access reviews, no more 'we'll need to get back to the auditor'.",
    moments: [
      {
        time: 'Monday',
        title: 'Quarterly review cadence kicks off',
        body: 'Monosync launches the access-review campaign with risk-tier prioritization. Managers and app owners get scoped review lists in their inbox; no master spreadsheet involved.',
      },
      {
        time: 'Wednesday',
        title: 'Auditor walkthrough',
        body: 'External auditor asks how privileged-database access is governed. You pull a signed evidence pack from Monopam: six months of brokered sessions, every keystroke logged. Walkthrough ends in 20 minutes.',
      },
      {
        time: 'Friday',
        title: 'SoD violation, caught at request time',
        body: 'A user with payment-approver entitlement tries to request payment-initiator. The Monosync SoD rule blocks the grant; the exception flow routes to compensating-controls review automatically.',
      },
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'Compliance as a steady state.',
    pillarsBody:
      'Four primitives that turn audit readiness into a continuous practice.',
    pillars: [
      {
        title: 'Framework-aligned controls, pre-built',
        description:
          'SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS, HIPAA: control mappings and report templates ship in the platform, not as a consulting engagement.',
        bullets: [
          'Per-framework report templates',
          'Pre-built control-to-evidence mapping',
          'Multi-jurisdiction data residency',
          'Configurable per-tenant retention',
        ],
      },
      {
        title: 'Audit logs your auditors can hold',
        description:
          'Immutable, tamper-evident logs with hash-chained integrity. Exports are signed, retain chain-of-custody, and can be replayed for investigation.',
        bullets: [
          'Hash-chained audit log entries',
          'Signed evidence bundle exports',
          'Long-retention configurable per tier',
          'Replayable session recordings',
        ],
      },
      {
        title: 'Access reviews that actually finish',
        description:
          'Schedule reviews by risk tier, manager, or app owner. Bulk-approve with diff highlighting; auto-revoke the unattested.',
        bullets: [
          'Risk-tier prioritized campaigns',
          'Diff highlighting on recent changes',
          'Auto-revoke on overdue attestation',
          'Manager and app-owner routing',
        ],
      },
      {
        title: 'Separation of duties, enforced',
        description:
          'Encode SoD rules. Monosync blocks conflicting access at request time and surfaces violations during reviews, before they become findings.',
        bullets: [
          'Configurable SoD rule sets',
          'Real-time violation blocking',
          'Compensating-controls exception flow',
          'Per-framework SoD reports',
        ],
      },
    ],
    helpEyebrow: 'Straight to the docs',
    helpTitle: 'Reference docs for the audit binder.',
    helpBody:
      'Deep-links to the help articles your audit prep actually needs.',
    helpGroups: [
      {
        title: 'Governance & reviews',
        links: [
          { label: 'Governance overview', description: 'Entitlement model and reviews', href: 'https://help.monofor.com/monosign/governance' },
          { label: 'Access certification', description: 'Schedule and run review campaigns', href: 'https://help.monofor.com/monosign/access-certification' },
          { label: 'Campaigns', description: 'Define review scope and cadence', href: 'https://help.monofor.com/monosign/campaigns' },
          { label: 'Reviewer experience', href: 'https://help.monofor.com/monosign/reviewer' },
        ],
      },
      {
        title: 'Access model',
        links: [
          { label: 'Access packages', description: 'Bundle entitlements by role', href: 'https://help.monofor.com/monosign/access-packages' },
          { label: 'Role definitions', href: 'https://help.monofor.com/monosign/role-definitions' },
          { label: 'Permission definitions', href: 'https://help.monofor.com/monosign/permission-definitions' },
          { label: 'Access restrictions', href: 'https://help.monofor.com/monosign/access-restrictions' },
        ],
      },
      {
        title: 'Audit & evidence',
        links: [
          { label: 'Audits', description: 'Immutable, exportable audit logs', href: 'https://help.monofor.com/monosign/audits' },
          { label: 'Subscribe to audits', description: 'Stream events to SIEM / GRC', href: 'https://help.monofor.com/monosign/subscribe-to-audits' },
          { label: 'Reports', description: 'Generate and export reports', href: 'https://help.monofor.com/monosign/reports' },
          { label: 'Monopam recordings', description: 'Session evidence with full transcript', href: 'https://help.monofor.com/monopam/recordings' },
        ],
      },
      {
        title: 'Sync & integration',
        links: [
          { label: 'Identity lifecycle (Monosync)', href: 'https://help.monofor.com/monosync/identity-lifecycle-management' },
          { label: 'SIEM-targeted audit export', href: 'https://help.monofor.com/monosync/how-to-sync-integration-logs-to-siem-based-on-the-' },
          { label: 'Monosync audits', href: 'https://help.monofor.com/monosync/audits' },
          { label: 'Monosign owners', description: 'Assigning app and access owners', href: 'https://help.monofor.com/monosign/owners' },
        ],
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Audit & compliance', description: 'Use case: continuous audit readiness.', href: '/solutions/audit-compliance' },
      { label: 'Identity governance', description: 'Cross-app entitlement mapping.', href: '/sync/governance' },
      { label: 'For Security Engineers', description: 'The control surface compliance leans on.', href: '/for/security-engineers' },
    ],
  },
  tr: {
    metaTitle: 'Uyum sorumluları için Monofor',
    metaDescription:
      'Çerçevelerle hizalı kontroller, kurcalamaya karşı korumalı denetim, sonuçlanan erişim gözden geçirmeleri ve uygulanan görev ayrılığı: denetim yanıtını üç aylık yangın tatbikatından istikrarlı bir düzene çevirin.',
    heroEyebrow: 'Uyum sorumluları için',
    heroTitle: ['Denetime hazır,', 'denetim telaşından uzak.'],
    heroBody:
      'Çerçevelerle hizalı kontroller, kurcalamaya karşı korumalı denetim logları, zamanlanmış erişim gözden geçirmeleri ve uygulanan görev ayrılığı: denetçilerin gerçek çalışma biçimine göre tasarlanmış tek platformda.',
    badges: ['SOC 2 / ISO 27001 / GDPR / KVKK', 'Kurcalamaya karşı korumalı kanıt', 'Görev ayrılığı (SoD) uygulaması'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    routineEyebrow: 'Monofor ile bir gün',
    routineTitle: 'Bir uyum sorumlusunun tipik haftası.',
    routineBody:
      'Artık denetim haftası yangın tatbikatları yok, tablolarla yürüyen erişim gözden geçirmeleri yok, "denetçiye sonra döneriz" yok.',
    moments: [
      {
        time: 'Pazartesi',
        title: 'Üç aylık gözden geçirme düzeni başlıyor',
        body: 'Monosync, risk kademesi önceliklendirmesiyle erişim gözden geçirme kampanyasını başlatıyor. Yöneticiler ve uygulama sahipleri, kapsamı belirlenmiş gözden geçirme listelerini gelen kutularında buluyor; ortada ana tablo yok.',
      },
      {
        time: 'Çarşamba',
        title: 'Denetçi incelemesi',
        body: 'Dış denetçi, ayrıcalıklı veritabanı erişiminin nasıl yönetildiğini soruyor. Monopam’dan imzalı bir kanıt paketi çekiyorsunuz: altı aylık aracılı oturumlar, her tuş vuruşu loglanmış. İnceleme 20 dakikada bitiyor.',
      },
      {
        time: 'Cuma',
        title: 'Talep anında yakalanan SoD ihlali',
        body: 'Ödeme onaylayıcı yetkisine sahip bir kullanıcı, ödeme başlatıcı yetkisi talep ediyor. Monosync SoD kuralı atamayı engelliyor; istisna akışı otomatik olarak telafi edici kontrol incelemesine yönlendiriliyor.',
      },
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'İstikrarlı bir düzen olarak uyum.',
    pillarsBody:
      'Denetim hazırlığını sürekli bir pratiğe dönüştüren dört yapı taşı.',
    pillars: [
      {
        title: 'Çerçevelerle hizalı, hazır kontroller',
        description:
          'SOC 2, ISO 27001, GDPR, KVKK, PCI-DSS, HIPAA: kontrol eşlemeleri ve rapor şablonları bir danışmanlık projesi olarak değil, platformla birlikte gelir.',
        bullets: [
          'Çerçeve bazında rapor şablonları',
          'Hazır kontrol-kanıt eşlemesi',
          'Çok bölgeli veri yerleşimi',
          'Kiracı bazında yapılandırılabilir saklama',
        ],
      },
      {
        title: 'Denetçilerinizin güvenebileceği denetim logları',
        description:
          'Hash zinciriyle bütünlüğü korunan, değiştirilemez ve kurcalamaya karşı korumalı loglar. Dışa aktarımlar imzalıdır, gözetim zincirini korur ve soruşturma için yeniden oynatılabilir.',
        bullets: [
          'Hash zincirli denetim logu kayıtları',
          'İmzalı kanıt paketi dışa aktarımları',
          'Katman bazında yapılandırılabilir uzun saklama',
          'Yeniden oynatılabilir oturum kayıtları',
        ],
      },
      {
        title: 'Gerçekten sonuçlanan erişim gözden geçirmeleri',
        description:
          'Gözden geçirmeleri risk kademesine, yöneticiye veya uygulama sahibine göre zamanlayın. Fark vurgulamayla toplu onaylayın; doğrulanmayanları otomatik iptal edin.',
        bullets: [
          'Risk kademesine göre önceliklendirilen kampanyalar',
          'Son değişikliklerde fark vurgulama',
          'Süresi geçen onaylarda otomatik iptal',
          'Yönetici ve uygulama sahibi yönlendirmesi',
        ],
      },
      {
        title: 'Uygulanan görev ayrılığı',
        description:
          'SoD kurallarını tanımlayın. Monosync çakışan erişimi talep anında engeller ve gözden geçirmelerde ihlalleri bulguya dönüşmeden önce öne çıkarır.',
        bullets: [
          'Yapılandırılabilir SoD kural setleri',
          'Gerçek zamanlı ihlal engelleme',
          'Telafi edici kontroller için istisna akışı',
          'Çerçeve bazında SoD raporları',
        ],
      },
    ],
    helpEyebrow: 'Doğrudan dokümantasyona',
    helpTitle: 'Denetim dosyası için referans dokümanlar.',
    helpBody:
      'Denetim hazırlığınızın gerçekten ihtiyaç duyduğu yardım makalelerine derin bağlantılar.',
    helpGroups: [
      {
        title: 'Yönetişim ve gözden geçirmeler',
        links: [
          { label: 'Yönetişime genel bakış', description: 'Yetki modeli ve gözden geçirmeler', href: 'https://help.monofor.com/monosign/governance' },
          { label: 'Erişim sertifikasyonu', description: 'Gözden geçirme kampanyalarını zamanlayın ve yürütün', href: 'https://help.monofor.com/monosign/access-certification' },
          { label: 'Kampanyalar', description: 'Gözden geçirme kapsamını ve düzenini tanımlayın', href: 'https://help.monofor.com/monosign/campaigns' },
          { label: 'Gözden geçiren deneyimi', href: 'https://help.monofor.com/monosign/reviewer' },
        ],
      },
      {
        title: 'Erişim modeli',
        links: [
          { label: 'Erişim paketleri', description: 'Yetkileri role göre paketleyin', href: 'https://help.monofor.com/monosign/access-packages' },
          { label: 'Rol tanımları', href: 'https://help.monofor.com/monosign/role-definitions' },
          { label: 'İzin tanımları', href: 'https://help.monofor.com/monosign/permission-definitions' },
          { label: 'Erişim kısıtlamaları', href: 'https://help.monofor.com/monosign/access-restrictions' },
        ],
      },
      {
        title: 'Denetim ve kanıt',
        links: [
          { label: 'Denetimler', description: 'Değiştirilemez, dışa aktarılabilir denetim logları', href: 'https://help.monofor.com/monosign/audits' },
          { label: 'Denetimlere abone olma', description: 'Olayları SIEM / GRC’ye aktarın', href: 'https://help.monofor.com/monosign/subscribe-to-audits' },
          { label: 'Raporlar', description: 'Rapor oluşturun ve dışa aktarın', href: 'https://help.monofor.com/monosign/reports' },
          { label: 'Monopam kayıtları', description: 'Tam dökümüyle oturum kanıtı', href: 'https://help.monofor.com/monopam/recordings' },
        ],
      },
      {
        title: 'Senkronizasyon ve entegrasyon',
        links: [
          { label: 'Kimlik yaşam döngüsü (Monosync)', href: 'https://help.monofor.com/monosync/identity-lifecycle-management' },
          { label: 'SIEM hedefli denetim dışa aktarımı', href: 'https://help.monofor.com/monosync/how-to-sync-integration-logs-to-siem-based-on-the-' },
          { label: 'Monosync denetimleri', href: 'https://help.monofor.com/monosync/audits' },
          { label: 'Monosign sahipleri', description: 'Uygulama ve erişim sahiplerinin atanması', href: 'https://help.monofor.com/monosign/owners' },
        ],
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Denetim ve uyum', description: 'Kullanım senaryosu: sürekli denetim hazırlığı.', href: '/solutions/audit-compliance' },
      { label: 'Kimlik yönetişimi', description: 'Uygulamalar arası yetki eşlemesi.', href: '/sync/governance' },
      { label: 'Güvenlik mühendisleri için', description: 'Uyumun dayandığı kontrol yüzeyi.', href: '/for/security-engineers' },
    ],
  },
}

type Props = { locale: string }

export default function CompliancePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/for/compliance-officers"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <PersonaRoutine
          eyebrow={c.routineEyebrow}
          title={c.routineTitle}
          body={c.routineBody}
          moments={c.moments}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={c.pillarsTitle}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <PersonaHelpLinks
          eyebrow={c.helpEyebrow}
          title={c.helpTitle}
          body={c.helpBody}
          groups={c.helpGroups}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
